import { useCallback, useEffect, useState } from 'react';
import { type UniversalIssue } from '@deque/axe-devtools-issues-utils';
import { getSentIssuesByIds } from '../utils/clients/walnutApi';
import { handleAsyncState } from '../utils/handleAsyncState';
import type { SendToIntegrationContainerProps } from './useSendToIntegrationLogic';
import type { IssueMetadata } from '../types/clients';

const fetchIssuesMetadataByChunk = async (
  issueIds: string[],
  authFn: () => Promise<string>,
  apiHostname: string,
  enterpriseId: string
) => {
  const bearerToken = await authFn();

  const chunkSize = 10;
  const issueIdsChunks: string[][] = [];

  for (let i = 0; i < issueIds.length; i += chunkSize) {
    issueIdsChunks.push(issueIds.slice(i, i + chunkSize));
  }

  const requestedIssuesMetadata = (
    await Promise.all(
      issueIdsChunks.map(chunk =>
        getSentIssuesByIds({
          apiHostname,
          token: bearerToken,
          enterpriseId,
          issueIds: chunk
        })
      )
    )
  ).flat();

  return requestedIssuesMetadata;
};

export const useGetIssuesData = (
  sendToIntegrationProps: SendToIntegrationContainerProps
) => {
  const [issues, setIssues] = useState<UniversalIssue[]>([]);
  const [issuesIDs, setIssuesIDs] = useState<string[]>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError] = useState<Error | null>(null);
  const [sentIssues, setSentIssues] = useState<IssueMetadata[]>([]);

  useEffect(() => {
    if (!sendToIntegrationProps.userEnterpriseId) {
      return;
    }

    async function loadIssues() {
      const allIssues = await sendToIntegrationProps.issuesFn();
      const issuesWithIds = allIssues[0].issueId
        ? allIssues
        : allIssues.map(issueEntity => ({
            ...issueEntity,
            issueId: sendToIntegrationProps.issueIDExtractorFn(issueEntity)
          }));

      const issuesIDList = allIssues.map(issue =>
        sendToIntegrationProps.issueIDExtractorFn(issue)
      );

      setIssues(issuesWithIds);
      setIssuesIDs(issuesIDList);

      if (!issuesIDList.length) {
        return;
      }
      const requestedIssuesMetadata = await fetchIssuesMetadataByChunk(
        issuesIDList,
        sendToIntegrationProps.authFn,
        sendToIntegrationProps.apiHostname,
        sendToIntegrationProps.userEnterpriseId
      );

      setSentIssues(requestedIssuesMetadata);
    }

    handleAsyncState(loadIssues, setIssuesLoading, setIssuesError);
  }, [
    sendToIntegrationProps.issuesFn,
    sendToIntegrationProps.userEnterpriseId
  ]);

  const refetchIssues = useCallback(async () => {
    try {
      setIssuesLoading(true);
      const requestedIssuesMetadata = await fetchIssuesMetadataByChunk(
        issuesIDs,
        sendToIntegrationProps.authFn,
        sendToIntegrationProps.apiHostname,
        sendToIntegrationProps.userEnterpriseId
      );

      setSentIssues(requestedIssuesMetadata);
    } catch (error) {
      setIssuesError(error as Error);
    } finally {
      setIssuesLoading(false);
    }
  }, [
    issuesIDs,
    sendToIntegrationProps.authFn,
    sendToIntegrationProps.apiHostname,
    sendToIntegrationProps.userEnterpriseId
  ]);

  return {
    issues,
    issuesIDs,
    sentIssues,
    issuesLoading,
    issuesError,
    refetchIssues
  };
};
